// The console's keyboard (APS-195, docs/brand.md "Keyboard"): ⌘K opens Find, ? the list of
// keys, and on a list j/k move between rows, Enter opens one (x selects and e resolves on Issues).
// A key belongs to the page only when nothing else wants it: no modifier, no field being typed
// in, no dialog open.
import { type RefObject, useEffect } from 'react'

/** Whether the page's shortcuts leave this key press alone. */
export function shortcutOff(e: KeyboardEvent): boolean {
  if (e.metaKey || e.ctrlKey || e.altKey || typingIn(e.target)) return true
  // A modal dialog (Find, a confirmation) has the keyboard to itself.
  return document.querySelector('dialog[open]') !== null
}

/** ⌘ on Apple's systems, Ctrl elsewhere, as Find's key is written. */
export function findKey(): string {
  const platform =
    typeof navigator === 'undefined' ? '' : `${navigator.platform} ${navigator.userAgent}`
  return /Mac|iPhone|iPad/.test(platform) ? '⌘K' : 'Ctrl K'
}

/**
 * j and k on a table: focus the next or previous row's first link or button, so Enter opens it
 * as it would on a click. The row with focus is the current one (components.css `.t[data-row-keys] tr:focus-within`).
 */
export function useRowKeys(table: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.key !== 'j' && e.key !== 'k') || shortcutOff(e)) return
      const rows = [...(table.current?.querySelectorAll('tbody tr') ?? [])]
      // A row's first link or button; a row that opens itself (components/d HovRow) has none.
      const targets = rows
        .map(
          (row) =>
            row.querySelector<HTMLElement>('a[href], button:not(:disabled)') ??
            (row.hasAttribute('tabindex') ? (row as HTMLElement) : null),
        )
        .filter((el): el is HTMLElement => el !== null)
      if (targets.length === 0) return
      e.preventDefault()
      const at = targets.findIndex((el) => el.closest('tr')?.contains(document.activeElement))
      const next =
        at < 0 ? 0 : e.key === 'j' ? Math.min(at + 1, targets.length - 1) : Math.max(at - 1, 0)
      targets[next]?.focus()
      targets[next]?.scrollIntoView?.({ block: 'nearest' })
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [table])
}

/** Whether a key press belongs to a field being typed in, so shortcuts leave it alone. */
export function typingIn(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  // Esc shuts a dialog at once but leaves focus in its field until the dialog is taken away, a
  // task later: a key pressed then (? for the keys, after Find) is no longer typing (APS-575).
  if (target.closest('dialog:not([open])')) return false
  if (target.isContentEditable) return true
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return true
  return (
    target instanceof HTMLInputElement &&
    !['checkbox', 'radio', 'button', 'submit', 'reset'].includes(target.type)
  )
}
